import React, { useMemo } from 'react';

import { MultiseriesApi } from '@candu-ai/analytics/content-events';
import { AnalyticQuery } from '@candu-ai/analytics/types';
import { TimeChart, TimeChartProps } from '@candu-ai/analytics/ui';

interface TimeChartFromRawDataProps<T = { date: string; [e: string]: unknown }>
  extends Omit<TimeChartProps, 'chartData' | 'xAxisDataKey' | 'entries'> {
  rawData?: AnalyticQuery<T>;
  multiseriesApi?: MultiseriesApi;
  isLoading?: boolean;
}

const GroupByContentTimeChart = ({
  rawData,
  multiseriesApi,
  ...otherProps
}: TimeChartFromRawDataProps) => {
  const [groupById, xAxis, yAxis] = rawData?.fields || [];

  const series = multiseriesApi?.getSeriesIds() || [];
  const entries = multiseriesApi?.getEntries() || [];

  const chartData = useMemo(() => {
    if (!rawData?.metadata) {
      return [];
    }

    // create a date xAxis from time of the first entry
    const xLine = rawData.metadata.time.timestamps || [];

    const records: any = {};
    rawData.rows.forEach((row) => {
      const seriesItem = row[groupById.name] as string;
      const date = row[xAxis.name] as string;
      const value = row[yAxis.name];
      if (!records[seriesItem]) {
        records[seriesItem] = {};
      }
      records[seriesItem][date] = value;
    });

    return xLine?.map((xEntry) => ({
      [xAxis?.name]: xEntry,
      ...Object.fromEntries(series.map((s) => [s, (records?.[s] || {})[xEntry] ?? 0])),
    }));
  }, [rawData, xAxis, yAxis, series, entries]);

  return (
    <TimeChart
      executedAt={rawData?.executedAt}
      chartData={chartData}
      entries={entries}
      xAxisDataKey={xAxis?.name}
      period={rawData?.metadata.time.period}
      isMultiSeries={!!multiseriesApi}
      {...otherProps}
    />
  );
};

const TwoDContentTimeChart = ({ rawData, ...otherProps }: TimeChartFromRawDataProps) => {
  const [xAxis, yAxis] = rawData?.fields || [];

  const chartData = useMemo(() => {
    if (!rawData?.metadata) {
      return [];
    }

    const dataTimestampLookup = Object.fromEntries(
      rawData.rows.map((row) => [row[xAxis?.name], row[yAxis?.name]]),
    );

    // create a date xAxis from time of the first entry
    const timestamps = rawData.metadata.time.timestamps || [];

    return timestamps?.map((timestamp) => ({
      [xAxis?.name]: timestamp,
      [yAxis?.name]: dataTimestampLookup[timestamp] ?? 0,
    }));
  }, [rawData, xAxis, yAxis]);

  if (!chartData.length) {
    return null;
  }

  return (
    <TimeChart
      executedAt={rawData?.executedAt}
      chartData={chartData}
      entries={[{ value: yAxis?.name, label: yAxis?.name }]}
      xAxisDataKey={xAxis?.name}
      period={rawData?.metadata.time.period}
      {...otherProps}
    />
  );
};

export const ContentTimeChart = ({ rawData, ...otherProps }: TimeChartFromRawDataProps) => {
  if (!otherProps.isLoading && !rawData) {
    // failed to fetch data
    return null;
  }

  const isGroupByChart = (rawData?.fields.length || 0) > 2;
  if (isGroupByChart) {
    return <GroupByContentTimeChart rawData={rawData} {...otherProps} />;
  }
  return <TwoDContentTimeChart rawData={rawData} {...otherProps} />;
};
